import React from 'react';
import { BoxProps, HStack, Box } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { PropsWithChildren } from 'react';
import { Tooltip } from '../../common/components/tooltip';
import { eventNotificationPriorityToColors } from '../../event-notifications/event-notifications';
import {
    useAllEventNotifications,
    useEventNotifications,
    useRemoveEventNotifications,
} from '../../event-notifications/use-event-notifications';
import {
    columnDefinitionNameToColumnGroup,
    columnGroupToEventNotificationNames,
} from '../column-definition.enum';
import { useQueueStore } from '../context/use-queue-store';
import { useQueueCell } from './queue-cell-context';
import { useEventNotificationDisplayNames } from '../../event-notifications/use-event-notification-display-names';
import { useBroadcast } from '../../common/broadcast-channel/use-broadcast';
import { QUEUE_ROW_UPDATE_BOX_WIDTH_PX } from './queue-header-row';
import {
    CadNextNotification,
    CadNextNotificationPriorityEnumType,
    CadNextNotificationTypeEnumType,
} from '@mark43/cad-api';
import { EventNotificationsCounter } from '../../event-panel/tabs/components/event-notifications-counter';
import { getCounterNotifications } from '../../event-panel/tabs/components/get-counter-notifications';
import { highestNotificationPriorityForNotifications } from '../../event-notifications/highest-notification-priority-for-notifications';

function getNotificationTextColor(eventNotifications: CadNextNotification[]): string {
    const highestNotificationPriority = highestNotificationPriorityForNotifications(
        eventNotifications
    );
    if (highestNotificationPriority) {
        return eventNotificationPriorityToColors[highestNotificationPriority].notchOrText;
    }
    return 'text.primary';
}

export const BaseQueueCell = observer(
    React.forwardRef(function BaseQueueCell(
        props: PropsWithChildren<
            {
                eventNotificationPriority?: CadNextNotificationPriorityEnumType;
                alignment?: 'LEFT' | 'CENTER' | 'RIGHT';
            } & BoxProps
        >,
        ref: React.ForwardedRef<HTMLElement>
    ): React.ReactElement {
        const {
            alignment,
            children,
            eventNotificationPriority: notificationPriority,
            ...boxProps
        } = props;
        const queueStore = useQueueStore();
        const { postMessage } = useBroadcast('dismiss-event-notifications');
        const { index, eventId, columnDefinitionValue } = useQueueCell();
        const eventNotificationDisplayNames = useEventNotificationDisplayNames();
        const removeEventNotifications = useRemoveEventNotifications();
        // We don't actually want to subscribe to all event notifications for the event unless this is the zero-indexed cell,
        // so we pass in -1 (which will always miss) otherwise
        const allEventNotifications = useAllEventNotifications(index === 0 ? eventId : -1);
        const queueIsShowingUpdates = queueStore.workspacePanel.queue.showUpdates;
        const notificationsForCell = useEventNotifications({
            notifications: columnDefinitionValue.eventNotificationNames,
        });
        const onClickMemo = React.useCallback(() => {
            if (
                columnDefinitionValue.eventNotificationNames &&
                notificationsForCell.length > 0 &&
                queueIsShowingUpdates
            ) {
                // This dismisses event panel notifications in the old CAD
                postMessage({
                    type: 'DISMISS_LEGACY_CAD_EVENT_PANEL_NOTIFICATIONS',
                    payload: {
                        eventId,
                        columnDefinitionName: columnDefinitionValue.name,
                    },
                });
                let notificationsToRemove: CadNextNotificationTypeEnumType[] = [];
                const columnGroup = columnDefinitionNameToColumnGroup[columnDefinitionValue.name];
                if (columnGroup) {
                    for (const CadNextNotificationTypeEnumType of columnGroupToEventNotificationNames[
                        columnGroup
                    ]) {
                        notificationsToRemove.push(CadNextNotificationTypeEnumType);
                    }
                } else {
                    notificationsToRemove = notificationsToRemove.concat(
                        columnDefinitionValue.eventNotificationNames
                    );
                }
                removeEventNotifications({
                    notifications: notificationsToRemove,
                });
            }
        }, [
            columnDefinitionValue.eventNotificationNames,
            columnDefinitionValue.name,
            notificationsForCell.length,
            queueIsShowingUpdates,
            postMessage,
            eventId,
            removeEventNotifications,
        ]);
        return (
            <Box
                ref={ref}
                onClick={onClickMemo}
                display={props.display ?? 'inline-block'}
                className="base-queue-cell"
                width="100%"
                px="10px"
                overflow="hidden"
                cursor="default"
                userSelect="none"
                color={
                    queueIsShowingUpdates && notificationsForCell.length > 0
                        ? getNotificationTextColor(notificationsForCell)
                        : 'inherit'
                }
                textOverflow="ellipsis"
                textAlign={
                    alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'right' : 'left'
                }
                textTransform="uppercase"
                whiteSpace="nowrap"
                fontWeight={
                    queueIsShowingUpdates && notificationsForCell.length > 0 ? 'bold' : undefined
                }
                {...boxProps}
            >
                {queueIsShowingUpdates && index === 0 ? (
                    <HStack>
                        {allEventNotifications.length > 0 ? (
                            <Tooltip
                                overlay={getCounterNotifications(allEventNotifications)
                                    .map((eventNotification) =>
                                        eventNotification.notificationPriority === 'HIGH'
                                            ? `High Priority ${
                                                  eventNotificationDisplayNames[
                                                      eventNotification.notificationType
                                                  ]
                                              }`
                                            : eventNotificationDisplayNames[
                                                  eventNotification.notificationType
                                              ]
                                    )
                                    .join(', ')}
                            >
                                <div>
                                    <EventNotificationsCounter
                                        notifications={allEventNotifications}
                                    />
                                </div>
                            </Tooltip>
                        ) : (
                            <Box w={QUEUE_ROW_UPDATE_BOX_WIDTH_PX} />
                        )}
                        <Box
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            minWidth={0}
                        >
                            {children}
                        </Box>
                    </HStack>
                ) : (
                    children
                )}
            </Box>
        );
    })
);
